import { canReadDomain } from '@/api/core/authorization';
import { readEzremsTransitionPreview } from '@/api/core/ezrems-transition-preview';
import { readRoomPricing } from '@/api/core/native-midterm-preparation';
import {
  changeRoomFirstMoveInDate,
  type FirstMoveInDateCommand,
} from '@/api/core/room-first-move-in-date';
import { readRoomSale, type RoomSaleAdminDetail } from '@/api/core/room-sales';
import { isApiError } from '@/api/http/errors';
import { isUuid } from '@/lib/validation';
import { Button, DatePicker, Input } from '@mgrvtechgroup/admin-ds';
import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { pendingEzremsTransition } from './ezrems-transition-preview-section';
import {
  beforeFirstDateBound,
  firstDateBound,
  firstDateBoundMessage,
  type FirstDateBound,
} from './first-date-lower-bound';
import { dateRefusalText, firstDateRefusal } from './first-date-refusal';
import type { PropertyRuntime } from './property-queries';
import { hasRoomSalesReadAccess } from './room-sales-queries';
interface Held {
  namespace: string;
  generation: number;
  roomId: string;
  roomName: string;
  actor: string;
  body: FirstMoveInDateCommand;
  sale: RoomSaleAdminDetail;
  pricing: Awaited<ReturnType<typeof readRoomPricing>>;
  accepted: boolean;
}
interface DateOwner {
  busy: boolean;
  held: Held | null;
  version: number;
  listeners: Set<() => void>;
}
const owners = new WeakMap<
  PropertyRuntime['session'],
  WeakMap<PropertyRuntime['client'], DateOwner>
>();
export function firstDateOwner(runtime: PropertyRuntime): DateOwner {
  let clients = owners.get(runtime.session);
  if (!clients) {
    clients = new WeakMap();
    owners.set(runtime.session, clients);
  }
  let owner = clients.get(runtime.client);
  if (!owner) {
    owner = { busy: false, held: null, version: 0, listeners: new Set() };
    clients.set(runtime.client, owner);
  }
  return owner;
}
const publish = (owner: DateOwner) => {
  owner.version++;
  owner.listeners.forEach((listener) => {
    listener();
  });
};
export function useFirstDateOwner(runtime: PropertyRuntime) {
  const owner = firstDateOwner(runtime);
  useSyncExternalStore(
    (listener) => {
      owner.listeners.add(listener);
      return () => {
        owner.listeners.delete(listener);
      };
    },
    () => owner.version,
  );
  return owner;
}
export const firstDateBlocked = (runtime: PropertyRuntime) => {
  const owner = firstDateOwner(runtime);
  return owner.busy || owner.held !== null;
};
const preservedSale = (before: RoomSaleAdminDetail, after: RoomSaleAdminDetail) =>
  (
    [
      'room_id',
      'product',
      'channel',
      'daily_price_minor',
      'price_currency',
      'price_source',
      'price_revision',
      'midterm_reentry_blocked',
    ] as const
  ).every((key) => before[key] === after[key]) && after.pending_outcome === null;
export function RoomFirstMoveInDateEditor({
  runtime,
  generation,
  roomId,
  roomName,
  propertyId,
  typeId,
  detail,
  ready,
  active,
  blocked,
  isBlocked,
  refresh,
}: {
  runtime: PropertyRuntime;
  generation: number;
  roomId: string;
  roomName: string;
  propertyId: string;
  typeId: string;
  detail?: RoomSaleAdminDetail;
  ready: boolean;
  active: boolean;
  blocked: boolean;
  isBlocked: () => boolean;
  refresh: () => Promise<unknown>;
}) {
  const owner = useFirstDateOwner(runtime);
  const auth = useSyncExternalStore(runtime.session.subscribe, runtime.session.getSnapshot);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [feedback, setFeedback] = useState('');
  // BR-04 하한: 전환 preview의 earliest_date. 읽지 못하면 하한을 모르는 것으로 두고 서버가 거절하는 결과를 그대로 보여 준다.
  const [bound, setBound] = useState<FirstDateBound | null>(null);
  const context = JSON.stringify([
    runtime.apiNamespace,
    generation,
    propertyId,
    typeId,
    roomId,
    active,
    auth,
  ]);
  const lifetime = useRef({ context, epoch: 0, mounted: true });
  if (lifetime.current.context !== context) {
    lifetime.current.context = context;
    lifetime.current.epoch++;
  }
  useEffect(() => {
    const owned = lifetime.current;
    owned.mounted = true;
    return () => {
      owned.mounted = false;
      owned.epoch++;
    };
  }, []);
  useEffect(() => {
    setDate(detail?.first_move_in_date ?? '');
    setReason('');
    setFeedback('');
  }, [roomId, runtime.apiNamespace, generation, detail?.first_move_in_date]);
  const permitted = () => {
    const snapshot = runtime.session.getSnapshot();
    return (
      hasRoomSalesReadAccess(runtime, generation) &&
      snapshot.status === 'authorized' &&
      snapshot.generation === generation &&
      !!snapshot.authorization?.assigned &&
      (snapshot.authorization.role_bits & 12) !== 0 &&
      !snapshot.deniedDomains.includes('booking') &&
      canReadDomain(snapshot.authorization, 'booking', 32) &&
      canReadDomain(snapshot.authorization, 'booking', 2)
    );
  };
  const eligible =
    ready &&
    detail?.allowed_actions.includes('change_first_move_in_date') &&
    detail.product === 'midterm' &&
    detail.channel === 'own_website' &&
    detail.pending_outcome === null;
  const heldVisible =
    owner.held?.namespace === runtime.apiNamespace &&
    owner.held.generation === generation &&
    permitted();
  const readBound = async (): Promise<FirstDateBound> => {
    try {
      return firstDateBound(await readEzremsTransitionPreview(runtime.client, roomId));
    } catch {
      return { kind: 'unknown' };
    }
  };
  const showEditor = active && permitted() && !!eligible && !owner.held;
  useEffect(() => {
    if (!showEditor) {
      setBound(null);
      return;
    }
    let current = true;
    void readBound().then((value) => {
      if (current) setBound(value);
    });
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 편집기가 열릴 때와 판매 revision이 바뀔 때만 다시 읽는다.
  }, [showEditor, roomId, detail?.revision]);
  async function save(replay = false) {
    if (
      !active ||
      !permitted() ||
      owner.busy ||
      isBlocked() ||
      pendingEzremsTransition(runtime, generation)
    )
      return;
    const original = owner.held;
    if (
      replay
        ? !original || !heldVisible || original.roomId !== roomId
        : original ||
          !eligible ||
          !date ||
          date === detail?.first_move_in_date ||
          !reason.trim() ||
          Array.from(reason).length > 500
    )
      return;
    const epoch = lifetime.current.epoch;
    const owns = () => lifetime.current.mounted && lifetime.current.epoch === epoch && permitted();
    owner.busy = true;
    publish(owner);
    setFeedback('');
    let attempt = original;
    try {
      const actor = await runtime.client.get<{ user_id: unknown }>('users/me');
      if (
        !owns() ||
        !isUuid(actor.user_id) ||
        isBlocked() ||
        pendingEzremsTransition(runtime, generation)
      )
        return;
      if (original && original.actor !== actor.user_id) {
        setFeedback('현재 작업자가 달라 원래 요청을 다시 보낼 수 없습니다.');
        return;
      }
      if (!replay) {
        const sale = await readRoomSale(runtime.client, roomId);
        const pricing = await readRoomPricing(runtime.client, roomId);
        const freshBound = await readBound();
        if (!owns() || isBlocked() || pendingEzremsTransition(runtime, generation)) return;
        if (beforeFirstDateBound(freshBound, date)) {
          setBound(freshBound);
          setFeedback(
            `선택한 날짜가 너무 이릅니다. ${firstDateBoundMessage(freshBound)} 요청은 보내지 않았습니다.`,
          );
          return;
        }
        if (
          pricing.target.property_id !== propertyId ||
          (pricing.cas.target as { type_id?: string }).type_id !== typeId ||
          sale.revision !== detail?.revision ||
          !preservedSale(detail, sale) ||
          sale.first_move_in_date !== detail.first_move_in_date ||
          !sale.allowed_actions.includes('change_first_move_in_date')
        ) {
          setFeedback('현재 상태가 변경되었습니다. 다시 조회한 값을 확인한 뒤 저장하세요.');
          await refresh();
          return;
        }
        const actorAgain = await runtime.client.get<{ user_id: unknown }>('users/me');
        if (
          !owns() ||
          actorAgain.user_id !== actor.user_id ||
          isBlocked() ||
          pendingEzremsTransition(runtime, generation)
        )
          return;
        attempt = {
          namespace: runtime.apiNamespace,
          generation,
          roomId,
          roomName,
          actor: actor.user_id,
          body: Object.freeze({
            command_id: crypto.randomUUID(),
            expected_sale_revision: sale.revision,
            first_move_in_date: date,
            reason: reason.trim(),
          }),
          sale,
          pricing,
          accepted: false,
        };
        owner.held = attempt;
        publish(owner);
      }
      if (!attempt) return;
      if (!attempt.accepted) {
        await changeRoomFirstMoveInDate(runtime.client, attempt.roomId, attempt.body);
        attempt.accepted = true;
      }
      // The owner survives navigation and permission changes; only its original scope may settle it.
      if (!owns()) return;
      const after = await readRoomSale(runtime.client, attempt.roomId);
      const pricing = await readRoomPricing(runtime.client, attempt.roomId);
      if (!owns()) return;
      if (
        after.revision !== attempt.body.expected_sale_revision + 1 ||
        after.first_move_in_date !== attempt.body.first_move_in_date ||
        !preservedSale(attempt.sale, after) ||
        JSON.stringify(pricing) !== JSON.stringify(attempt.pricing)
      )
        throw new Error('readback mismatch');
      owner.held = null;
      publish(owner);
      setFeedback('첫 입주 가능일을 저장하고 가격·판매 유지 여부를 확인했습니다.');
      await refresh();
    } catch (error) {
      const terminal =
        isApiError(error) &&
        error.status !== undefined &&
        error.status >= 400 &&
        error.status < 500;
      if (!replay && !attempt?.accepted && terminal) {
        owner.held = null;
        publish(owner);
      }
      const refusal = firstDateRefusal(error);
      // 원 요청이 보존된 채(replay·접수 후·결과 불명) 거절되면 새 검토를 할 수 없다 — 보존 요청 확인만 안내한다.
      const kept = owner.held !== null;
      let boundRead = false;
      if (owns() && refusal === 'before-contract-end' && !kept) {
        const fresh = await readBound();
        setBound(fresh);
        boundRead = fresh.kind !== 'unknown';
      }
      if (owns()) {
        setFeedback(
          refusal !== null && kept
            ? dateRefusalText(refusal, 'held')
            : refusal === 'before-contract-end' || refusal === 'conflict'
              ? dateRefusalText(refusal, boundRead ? 'reread' : 'unverified')
              : refusal === 'unclassified'
                ? '현재 상태가 변경되었거나 날짜가 계약 준비 기간 전일 수 있습니다. 최신 날짜를 확인한 뒤 다시 저장하세요.'
                : kept
                  ? '원래 요청의 결과가 미확정입니다. 새 저장을 중단했습니다.'
                  : '첫 입주 가능일을 저장하지 못했습니다. 현재 상태를 다시 확인하세요.',
        );
        if (isApiError(error) && error.status === 403)
          runtime.session.reportDomainAccessDenied('booking', generation);
        else await refresh();
      }
    } finally {
      owner.busy = false;
      publish(owner);
    }
  }
  return (
    <div className="space-y-3">
      {showEditor && (
        <fieldset disabled={blocked || owner.busy} className="grid gap-3 sm:grid-cols-2">
          <div className="body-sm grid gap-1">
            첫 입주 가능일 변경
            {bound && <span className="text-muted-foreground">{firstDateBoundMessage(bound)}</span>}
            <DatePicker
              value={date ? new Date(`${date}T12:00:00`) : undefined}
              onChange={(value) => {
                setDate(
                  value
                    ? `${String(value.getFullYear())}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
                    : '',
                );
              }}
            />
          </div>
          <label className="body-sm grid gap-1">
            첫 입주 가능일 변경 사유
            <Input
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
          </label>
          <Button
            disabled={
              !date ||
              date === detail?.first_move_in_date ||
              (bound !== null && beforeFirstDateBound(bound, date)) ||
              !reason.trim() ||
              Array.from(reason).length > 500
            }
            onClick={() => {
              void save();
            }}
          >
            첫 입주 가능일 저장
          </Button>
        </fieldset>
      )}
      {owner.held && (
        <div role="status">
          <p>첫 입주 가능일 요청 결과를 먼저 확인하세요. 새 변경은 중단됩니다.</p>
          {heldVisible && (
            <p>
              {owner.held.roomName} · {owner.held.body.first_move_in_date} ·{' '}
              {owner.held.body.reason}
            </p>
          )}
          {heldVisible && active && owner.held.roomId === roomId && (
            <Button
              variant="outline"
              disabled={owner.busy || blocked}
              onClick={() => {
                void save(true);
              }}
            >
              원래 날짜 요청 결과 확인
            </Button>
          )}
        </div>
      )}
      {active && permitted() && feedback && <p role="status">{feedback}</p>}
    </div>
  );
}
